import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { OrderExecutionEngine } from '../../api/OrderExecutionEngine';
import { FiRefreshCw, FiGrid } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import { KanbanColumn } from './KanbanColumn';
import { KanbanCard } from './KanbanCard';
import ManualOrderModal from '../../components/modals/ManualOrderModal';
import { FiPlus } from 'react-icons/fi';

export default function OrderGestaoPage() {
    const navigate = useNavigate();
    const [board, setBoard] = useState({ PENDENTE: [], ACTIVE_FULL: [], CONCLUIDO: [], GOD_VIEW: [] });
    const [loading, setLoading] = useState(true);
    const [activeDragItem, setActiveDragItem] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);

    const loadData = async () => {
        setLoading(true);
        const res = await OrderExecutionEngine.obterQuadroOperacoes();
        if (res.success) setBoard(res.data);
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    // Helper to categorize orders
    const columnsData = [
        {
            id: 'col-pendentes',
            title: 'Triagem / Novos',
            colorClass: 'text-gray-400 border-gray-500',
            orders: (board.GOD_VIEW || []).filter(o => {
                // Mantém quem já é nativo da triagem
                if (o.subCategoriaInterna === 'AGUARDANDO_TRIAGEM' || o.status === 'ATRIBUIDO') return true;
                
                // Antiga lógica de Nascimentos/Órfãos: Pega o que não se encaixa nas outras colunas
                if (o.status === 'CONCLUIDO' || o.status === 'CANCELADO') return false;
                if (o.subCategoriaInterna === 'CLIENTE_DEVE_AGIR' || o.status === 'AGUARDANDO_DOCS' || o.status === 'REANALISE PERMITIDA') return false;
                if (o.subCategoriaInterna === 'CONSULTOR_DEVE_AGIR') return false;
                if (o.subCategoriaInterna === 'UNITY_DEVE_AGIR' || o.status?.includes('QUALIDADE')) return false;
                if (o.subCategoriaInterna === 'TIM_DEVE_AGIR') return false;
                if (o.subCategoriaInterna === 'LOGISTICA_ENTREGA') return false;
                
                return true; // Cai na malha fina e vira Triagem!
            })
        },
        {
            id: 'col-cliente',
            title: 'Ação do Cliente',
            colorClass: 'text-orange-400 border-orange-500',
            orders: (board.GOD_VIEW || []).filter(o => o.subCategoriaInterna === 'CLIENTE_DEVE_AGIR' || o.status === 'AGUARDANDO_DOCS' || o.status === 'REANALISE PERMITIDA')
        },
        {
            id: 'col-consultor',
            title: 'Ação Consultor',
            colorClass: 'text-blue-400 border-blue-500',
            orders: (board.GOD_VIEW || []).filter(o => o.subCategoriaInterna === 'CONSULTOR_DEVE_AGIR')
        },
        {
            id: 'col-unity',
            title: 'Análise Unity',
            colorClass: 'text-purple-400 border-purple-500',
            orders: (board.GOD_VIEW || []).filter(o => o.subCategoriaInterna === 'UNITY_DEVE_AGIR' || o.status?.includes('QUALIDADE'))
        },
        {
            id: 'col-tim',
            title: 'Análise TIM',
            colorClass: 'text-blue-600 border-blue-600',
            orders: (board.GOD_VIEW || []).filter(o => o.subCategoriaInterna === 'TIM_DEVE_AGIR')
        },
        {
            id: 'col-logistica',
            title: 'Logística',
            colorClass: 'text-yellow-400 border-yellow-500',
            orders: (board.GOD_VIEW || []).filter(o => o.subCategoriaInterna === 'LOGISTICA_ENTREGA')
        },
        {
            id: 'col-concluido',
            title: 'Concluído (Mês Atual)',
            colorClass: 'text-green-400 border-green-500',
            orders: (board.CONCLUIDO || []).filter(o => new Date(o.dataConclusao || o.updatedAt || o.createdAt).getMonth() === new Date().getMonth() && new Date(o.dataConclusao || o.updatedAt || o.createdAt).getFullYear() === new Date().getFullYear())
        }
    ];

    const handleStatusUpdate = async (id, status, comment, cat, sub, dataC) => {
        setLoading(true);
        const ok = await OrderExecutionEngine.atualizarStatus(id, status, comment, cat, sub, dataC);
        if (ok) { 
            toast.success("ALVO REPOSICIONADO"); 
            await loadData(); 
        } else {
            toast.error("FALHA AO ATUALIZAR");
            setLoading(false);
        }
    };

    const handleDragStart = (event) => {
        const { active } = event;
        const allOrders = [...board.PENDENTE, ...board.ACTIVE_FULL, ...board.GOD_VIEW, ...board.CONCLUIDO];
        const order = allOrders.find(o => o._id === active.id);
        setActiveDragItem(order);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveDragItem(null);
        
        if (!over) return;
        
        const orderId = active.id;
        const targetColId = over.id;
        const sourceColId = active.data.current?.colId;

        // Se o card não mudou de coluna, aborta o movimento silenciamente
        if (sourceColId === targetColId) return;

        // Determine new status based on target column mapping
        let newStatus = '';
        let newSubCat = '';
        
        switch (targetColId) {
            case 'col-cliente':
                newStatus = 'AGUARDANDO_DOCS';
                newSubCat = 'CLIENTE_DEVE_AGIR';
                break;
            case 'col-consultor':
                newStatus = 'PENDENCIA';
                newSubCat = 'CONSULTOR_DEVE_AGIR';
                break;
            case 'col-unity':
                newStatus = 'ANALISE QUALIDADE';
                newSubCat = 'UNITY_DEVE_AGIR';
                break;
            case 'col-tim':
                newStatus = 'EM ANALISE TIM';
                newSubCat = 'TIM_DEVE_AGIR';
                break;
            case 'col-logistica':
                newStatus = 'AGUARDANDO ENTREGA';
                newSubCat = 'LOGISTICA_ENTREGA';
                break;
            case 'col-concluido':
                newStatus = 'CONCLUIDO';
                newSubCat = 'FINALIZADO';
                break;
            case 'col-cancelado':
                newStatus = 'CANCELADO';
                newSubCat = 'ABORTADO';
                break;
            case 'col-pendentes':
                newStatus = 'PENDENTE';
                newSubCat = 'AGUARDANDO_TRIAGEM';
                break;
            default:
                return;
        }

        // Se for conclusão ou cancelamento, registra a data de hoje. Se não, null.
        const dataC = (newStatus === 'CONCLUIDO' || newStatus === 'CANCELADO') ? new Date().toISOString() : null;

        // Prompt de comentário (podendo deixar vazio)
        const comment = window.prompt(`Mover pedido para [${newStatus}]? Deixe um comentário:`, 'Movido via Kanban');
        if (comment === null) return; // User cancelled

        setLoading(true);
        const ok = await OrderExecutionEngine.atualizarStatus(orderId, newStatus, comment, 'GERAL', newSubCat, dataC);
        if (ok) { 
            toast.success("ALVO REPOSICIONADO"); 
            await loadData(); 
        } else {
            toast.error("FALHA AO MOVER ALVO");
            setLoading(false);
        }
    };

    return (
        <MainLayout>
            <div className="max-w-full mx-auto p-4 md:px-10 md:py-6 space-y-6 h-[90vh] flex flex-col">
                <header className="flex justify-between items-center border-b pb-6 border-[var(--border-color)] shrink-0">
                    <div className="pt-24 pb-12 px-6">
                        <h1 className="text-4xl md:text-6xl font-black text-[var(--text-primary)] uppercase tracking-tighter mb-4 text-center">
                            Geral <span className="text-[var(--accent-color)]">Kanban</span>
                        </h1>
                        <p className="text-[10px] font-bold uppercase tracking-[0.4em] opacity-30 mt-1 italic flex items-center gap-2">
                            <FiGrid /> Kanban Drag & Drop
                        </p>
                    </div>

                    <div className="flex items-center gap-2 bg-[var(--bg-card)] p-2 rounded-2xl border border-[var(--border-color)] shadow-xl overflow-x-auto no-scrollbar">
                        <button onClick={() => setShowOrderModal(true)} className="flex items-center gap-2 px-4 py-2 bg-[var(--accent-color)] hover:bg-yellow-400 text-black font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-[0_0_15px_rgba(234,179,8,0.3)] shrink-0">
                            <FiPlus size={16} /> NOVO PEDIDO MANUAL
                        </button>
                        <button onClick={loadData} className="ml-2 p-3 text-[var(--accent-color)] hover:rotate-180 transition-all active:scale-90 flex-shrink-0" title="Sincronizar Radar">
                            <FiRefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                </header>

                <div className="flex-1 overflow-x-auto overflow-y-hidden pb-4">
                    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} collisionDetection={closestCorners}>
                        <div className="flex gap-6 h-full items-start">
                            {columnsData.map(col => (
                                <KanbanColumn 
                                    key={col.id} 
                                    id={col.id} 
                                    title={col.title} 
                                    colorClass={col.colorClass} 
                                    orders={col.orders} 
                                    onUpdate={handleStatusUpdate}
                                />
                            ))}
                        </div>
                        <DragOverlay>
                            {activeDragItem ? <KanbanCard order={activeDragItem} /> : null}
                        </DragOverlay>
                    </DndContext>
                </div>
            </div>

            {showOrderModal && (
                <ManualOrderModal 
                    onClose={() => setShowOrderModal(false)}
                    onSuccess={() => {
                        setShowOrderModal(false);
                        loadData();
                    }}
                />
            )}
        </MainLayout>
    );
}